import { useEffect, useState } from 'react';
import { useVaultStore, VaultAuditEvent } from '../../../store/vaultStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Button';
import { Search } from 'lucide-react';

const OUTCOME_VARIANT: Record<string, any> = {
  succeeded: 'success', allowed: 'success', denied: 'destructive', failed: 'destructive',
};

export function VaultAudit() {
  const listAudit = useVaultStore((s) => s.listAudit);
  const [events, setEvents] = useState<VaultAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');

  const load = async () => {
    setLoading(true);
    try { setEvents(await listAudit({ action: action || undefined, limit: 100 })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="tracking-tight text-foreground font-display text-page-title">Audit log</h1>
        <p className="text-muted-foreground">Sensitive vault actions and permission decisions. Secret values are never recorded.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <h3 className="text-foreground font-display text-section-title">Events</h3>
            <div className="flex gap-2">
              <div className="relative w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Filter by action…" value={action} onChange={(e) => setAction(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
              </div>
              <Button variant="secondary" onClick={load}>Apply</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : events.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No audit events.</div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                <tr>
                  <th className="px-6 py-3">Time</th>
                  <th className="px-6 py-3">Action</th>
                  <th className="px-6 py-3">Outcome</th>
                  <th className="px-6 py-3">Actor</th>
                  <th className="px-6 py-3">Resource</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((e) => (
                  <tr key={e.id} className="hover:bg-secondary/20">
                    <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">{new Date(e.occurredAt).toLocaleString()}</td>
                    <td className="px-6 py-4 font-mono text-xs">{e.action}</td>
                    <td className="px-6 py-4"><Badge variant={OUTCOME_VARIANT[e.outcome] ?? 'secondary'}>{e.outcome}</Badge></td>
                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{e.actorUserId ? e.actorUserId.slice(0, 8) + '…' : '—'}</td>
                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                      {e.itemId ? `item ${e.itemId.slice(0, 8)}…` : e.vaultId ? `vault ${e.vaultId.slice(0, 8)}…` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
